import AddNegativeButtonRenderer from "@/components/cell-renderers/AddNegativeButtonRenderer";
import {
    ChangeInValueCellRenderer,
    FilterTextFormatter,
    ProgramTypeCellRenderer,
} from "@/components/cell-renderers/AllCellRenderers";
import AgGridLayout from "@/components/wrappers/layouts/AgGridLayout";
import { useGetModulesData } from "@/hooks/AllCustomHooks";
import { IMainContext, INegativeKeywordsData, IValueBoolean } from "@/ts/common-types";
import { customFilterOptions, mainContext } from "@/ts/constants";
import {
    negativeKeywordsSelectionChanged,
    numericValueFormatter,
    titleCase,
} from "@/ts/utils";
import { ValueFormatterParams } from "ag-grid-community";
import { useContext, useMemo } from "react";
import { useSelector } from "react-redux";

const SuggestedNegativeKeywords = () => {
    const { setSuggestedNegKwsGridApi, cerbosPermissions } =
        useContext<IMainContext>(mainContext);

    const suggestedNegKwsData = useSelector(
        (state: INegativeKeywordsData) => state.suggestedNegKwsData.value
    );

    const suggestedNegKwsLoading = useSelector(
        (state: { suggestedNegKwsLoading: IValueBoolean }) =>
            state.suggestedNegKwsLoading.value
    );

    const gridParams = useGetModulesData({ type: "grid-params" });

    const columnDefs = useGetModulesData({
        type: "columns",
        colDefs: [
            {
                field: "customer_search_term",
                headerName: "Search Term",
                minWidth: 300,
                maxWidth: 400,
                sort: "asc",
                filter: "agTextColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["text"],
                },
                checkboxSelection: true,
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                tooltipField: "custom_search_term",
                valueFormatter: (params: ValueFormatterParams) =>
                    titleCase(params.value),
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    pinnedColumnValue: "Total",
                },
            },
            {
                field: "",
                headerName: "Add As Negative",
                filter: false,
                suppressMenu: true,
                sortable: false,
                cellStyle: { textAlign: "center" },
                cellRenderer: AddNegativeButtonRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                pinned: "left",
            },
            {
                field: "ad_group_name",
                headerName: "Ad Group",
                filter: "agTextColumnFilter",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "adgroup_type",
                headerName: "Adgroup Type",
                filter: "agSetColumnFilter",
                hide: true,
                suppressMenu: true,
                sortable: false,
                cellStyle: { textAlign: "center" },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "nature",
                headerName: "Nature",
                filter: "agSetColumnFilter",
                suppressMenu: true,
                hide: true,
                sortable: false,
                cellStyle: { textAlign: "center" },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "program_type",
                headerName: "Ad Type",
                filter: "agSetColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                lockPinned: true,
                cellClass: ["lock-pinned"],
                maxWidth: gridParams["program_type"].maxWidth,
                minWidth: gridParams["program_type"].minWidth,
                cellRenderer: ProgramTypeCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                filterParams: {
                    valueFormatter: FilterTextFormatter
                },
            },
            {
                field: "campaign_name",
                headerName: "Campaign Name",
                filter: "agTextColumnFilter",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                filterParams: {
                    filterOptions: customFilterOptions["text"],
                },
            },
            {
                headerName: "Impressions",
                cellStyle: { whiteSpace: "normal" },
                field: "impression",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
            },
            {
                headerName: gridParams["click"]?.headerName || "Clicks",
                cellStyle: { whiteSpace: "normal" },
                field: "click",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Spends",
                field: "spend",
                filter: "agNumberColumnFilter",
                sort: "desc",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Orders",
                field: "orders",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                field: "sale",
                headerName: "Sales",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CTR",
                field: "ctr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
                hide: true,
            },
            {
                field: "cpc",
                headerName: gridParams["cpc"]?.headerName || "CPC",
                maxWidth: 170,
                minWidth: 120,
                lockPinned: true,
                cellClass: "lock-pinned",
                hide: true,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CR",
                field: "cr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                field: "cpa",
                headerName: "CPA",
                maxWidth: 170,
                minWidth: 120,
                lockPinned: true,
                cellClass: "lock-pinned",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "ACoS",
                field: "acos",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "RoAS",
                cellStyle: { whiteSpace: "normal" },
                field: "roas",
                filter: "agNumberColumnFilter",
                cellRenderer: ChangeInValueCellRenderer,
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
                hide: true,
            },
        ],
    });

    return (
        <div
            className="wrapper"
            style={{
                flex: "1 1 0",
                width: "100%",
                maxHeight: "calc(100% - 92px)",
            }}
        >
            <div
                id="negative-keywords-grid"
                className="ag-theme-alpine"
                style={{
                    height: "calc(100vh - 150px)",
                    width: "100%",
                }}
            >
                <AgGridLayout
                    rowData={suggestedNegKwsData}
                    columnDefs={columnDefs}
                    setGridApi={setSuggestedNegKwsGridApi}
                    onSelectionChanged={negativeKeywordsSelectionChanged}
                    loading={suggestedNegKwsLoading}
                    isTotalRowsReq={true}
                    rowHeight={gridParams?.rowHeight}
                />
            </div>
        </div>
    );
};

export default SuggestedNegativeKeywords;
